import React, { FC } from "react";
import { Box, Stack, Typography, Link } from "@mui/material";
import { SxProps, useTheme } from "@mui/material/styles";

import Logo from "@components/Logo";
import Social from "@components/Social";
import FooterLink from "./link";
import { MenuLink, SubMenu } from "@src/types";
import { Colors } from "@theme/colors";
import LayoutTwentyFour from "../LayoutTwentyFour";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";
import FeaturePopup from "../FeaturePopup";

interface Props {
  name?: string;
  menuLinks: Array<MenuLink>;
  footerBreak?: boolean;
  smallFooter?: boolean;
}

/**
 * As of April 2nd -- this function will create individual element containers
 * for each "section" in the nav. If `breakoutSubsections` is true (i.e. the solutions menu)
 * the individual sub sections will be the footer nav headers -- if `breakoutSubsections`
 * is false the menu items in the sub sections will be combined into one (i.e. the services menu)
 *
 * @param menuData The menu JSON from gatsby-config.ts
 * @returns
 */
const processMenuData = (menuData: Array<any>): Array<MenuLink> => {
  const dropdownLinks: Array<MenuLink> = [];

  menuData.forEach((item) => {
    if (item.type === "submenu" && item.subSections?.length > 0) {
      if (item?.breakoutSubsections === true) {
        item.subSections
          .filter((subSection: SubMenu) => {
            if (subSection?.layout !== "highlight") {
              return true;
            }

            return false;
          })
          .forEach((nestedItem: any) => {
            dropdownLinks.push(nestedItem);
          });
      } else {
        const newItem = { ...item, menuLinks: [] };

        item.subSections
          .filter((subSection: SubMenu) => {
            if (subSection?.layout !== "highlight") {
              return true;
            }

            return false;
          })
          .forEach((subSection: any) => {
            if (subSection.menuLinks?.length > 0) {
              subSection.menuLinks.forEach((menuLink: any) => {
                newItem.menuLinks.push({ ...menuLink });
              });
            }
          });

        dropdownLinks.push(newItem);
      }
    } else if (item.type === "dropdown" && item.menuLinks?.length > 0) {
      dropdownLinks.push(item);
    }
  });

  return dropdownLinks;
};

const SectionLinks: FC<Props> = ({ name, menuLinks, footerBreak = false }) => {
  const linkStyle = {
    color: Colors.TextSecondary,
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "uppercase",
    lineHeight: "20px",
    textDecoration: "none",
    letterSpacing: "0.5px",
    fontFamily: "Inter",
  };

  const breakStyles: SxProps = {
    height: "100%",
    breakBefore: "column",
  };

  return (
    <Stack
      key={`footer-column-${name}`}
      sx={{
        breakInside: "avoid",
        ...(footerBreak === true ? breakStyles : {}),
      }}
    >
      <Typography
        sx={{
          ...linkStyle,
          marginY: {
            xs: "12px",
            lg: "30px",
          },
        }}
      >
        {name}
      </Typography>
      <Stack
        sx={{
          gap: {
            xs: "12px",
            md: "16px",
            lg: "20px",
          },
        }}
      >
        {menuLinks?.map((item) => {
          if (item?.type && item?.type === "submenu-header") {
            return (
              <Typography
                key={`footer-sub-menu-header-${item.name}`}
                sx={{
                  ...linkStyle,
                }}
              >
                {item.name}
              </Typography>
            );
          }

          return (
            <div key={`footer-sub-wrap-${item.name}`}>
              <FooterLink
                key={`footer-sub-link-${item.name}`}
                data={item}
                level={1}
              />
            </div>
          );
        })}
      </Stack>
    </Stack>
  );
};

const Footer: FC<Props> = ({ menuLinks, smallFooter = false }) => {
  const thisYear = new Date().getFullYear();
  const dropdownLinks = processMenuData(menuLinks);

  const linkStateDefaultStyles = {
    opacity: 1,
    transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
  };

  const linkStateHoverStyles = {
    opacity: uiOpacityHover,
  };

  const footerTextStyles = {
    textDecoration: "none",
    fontFamily: "Inter",
    fontSize: "15px",
    fontWeight: 400,
    letterSpacing: "-0.25px",
    color: Colors.PrimaryContrast,
    marginTop: "0px !important",
    marginBottom: "0px !important",
  };

  const footerLinkStyles = {
    ...footerTextStyles,
    ...linkStateDefaultStyles,
    "&:hover": {
      ...linkStateHoverStyles,
    },
  };

  if (smallFooter) {
    return (
      <>
        <Box
          sx={{
            left: "0px",
            right: "0px",
            bottom: "0px",
            paddingY: "24px",
            backgroundColor: Colors.PrimaryContrast,
            width: "100%",
          }}
        >
          <LayoutTwentyFour>
            <Stack
              sx={{
                justifyContent: "space-between",
                flexDirection: "row",
              }}
            >
              <Typography>
                © {thisYear} Pangea. All Rights Reserved.
              </Typography>

              <Stack
                sx={{
                  gap: "24px",
                  flexDirection: "row",
                }}
              >
                <Link href="/privacy-choices/">Your Privacy Choices</Link>

                <Link href={"mailto:info@pangea.cloud"}>info@pangea.cloud</Link>
              </Stack>
            </Stack>
          </LayoutTwentyFour>
        </Box>

        <FeaturePopup />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          background: Colors.PrimaryContainerDark,
          zIndex: "4",
          paddingTop: "64px",
          fontFamily: "Inter",
        }}
      >
        <LayoutTwentyFour>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              paddingBottom: "32px",
              alignItems: "flex-start",
              flexDirection: {
                xs: "column",
                lg: "row",
              },
            }}
          >
            <Stack direction="column">
              <Box
                sx={{
                  color: "white",
                  svg: {
                    width: "160px",
                    height: "auto",
                  },
                }}
              >
                <Link href="/" sx={{ color: Colors.PrimaryContrast }}>
                  <Logo />
                </Link>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  margin: "16px 0",
                  color: Colors.PrimaryContrast,
                  fontSize: "18px",
                  letterSpacing: "-0.6%",
                  lineHeight: "1.5",
                  fontWeight: 400,
                }}
              >
                Security Guardrails for AI Applications.
              </Typography>
            </Stack>

            <Stack
              sx={{
                alignItems: "center",
                gap: {
                  xs: "16px",
                  sm: "24px",
                  xl: "30px",
                },
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
              }}
            >
              <Link
                href="/news-events/announcements/soc2-type2-audit/"
                sx={{
                  ...linkStateDefaultStyles,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "none",
                    ...linkStateHoverStyles,
                  },
                }}
              >
                <Stack alignItems="center" direction="row" gap="8px">
                  <Box
                    component="img"
                    src="/icons/soc2.png"
                    alt="SOC 2 Type I icon"
                    sx={{
                      height: "60px",
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: Colors.PrimaryContrast,
                      fontSize: "18px",
                    }}
                  >
                    SOC 2 Type 2
                  </Typography>
                </Stack>
              </Link>
              <Stack alignItems="center" direction="row" gap="8px">
                <Box
                  component="img"
                  src="/icons/hipaa-compliance.png"
                  alt="HIPAA compliance icon"
                  sx={{
                    height: "60px",
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: Colors.PrimaryContrast,
                    fontSize: "18px",
                  }}
                >
                  HIPAA Compliant
                </Typography>
              </Stack>
              <Stack alignItems="center" direction="row" gap="8px">
                <Box
                  component="img"
                  src="/icons/iso-iec-27001-compliance.png"
                  alt="ISO/IEC 27001 compliance icon"
                  sx={{
                    height: "60px",
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: Colors.PrimaryContrast,
                    fontSize: "18px",
                  }}
                >
                  ISO/IEC 27001
                </Typography>
              </Stack>
              <Stack alignItems="center" direction="row" gap="8px">
                <Box
                  component="img"
                  src="/icons/Sensiba-ISO-IEC-27701.png"
                  alt="ISO/IEC 27701 compliance icon"
                  sx={{
                    height: "60px",
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: Colors.PrimaryContrast,
                    fontSize: "18px",
                  }}
                >
                  ISO/IEC 27701
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </LayoutTwentyFour>

        <LayoutTwentyFour>
          <Box
            sx={{
              columns: {
                xs: 1,
                sm: 3,
                md: 5,
              },
              paddingBottom: "64px",
            }}
          >
            {dropdownLinks.map((section: any, idx: number) => {
              return (
                <SectionLinks
                  key={`footer-section-${section.name}`}
                  name={section.name}
                  footerBreak={section?.footerBreak}
                  menuLinks={section.menuLinks}
                />
              );
            })}
          </Box>
        </LayoutTwentyFour>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "flex-start", sm: "space-between" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{
            backgroundColor: Colors.PrimaryContainerDarkAccent,
            paddingY: "64px",
          }}
        >
          <LayoutTwentyFour>
            <Stack
              sx={{
                color: Colors.PrimaryContrast,
                gap: "24px",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                  lg: "column",
                },
                justifyContent: {
                  lg: "flex-start",
                  xs: "space-between",
                },
              }}
            >
              <Stack
                sx={{
                  flexDirection: {
                    xs: "column",
                    lg: "row",
                  },
                  gap: {
                    lg: "24px",
                    xs: "12px",
                  },
                  "*, Link, p, Typography": {
                    ...footerTextStyles,
                  },
                  a: {
                    opacity: 1,
                    transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
                    "&:hover": {
                      opacity: uiOpacityHover,
                    },
                  },
                  alignItems: "start",
                }}
                spacing={1}
              >
                <span>© {thisYear} Pangea. All rights reserved.</span>

                <Typography>636 Ramona St, Palo Alto, CA 94301</Typography>

                <Link href="/privacy-policy/" sx={footerLinkStyles}>
                  Privacy
                </Link>

                <Link href="/terms-of-use/" sx={footerLinkStyles}>
                  Terms of Use
                </Link>

                <Link href="/privacy-choices/" sx={footerLinkStyles}>
                  Your Privacy Choices
                </Link>

                <Link href="/contact-us/" sx={footerLinkStyles}>
                  Contact us
                </Link>
              </Stack>

              <Social iconStyle="footer" />
            </Stack>
          </LayoutTwentyFour>
        </Stack>
      </Box>

      <FeaturePopup />
    </>
  );
};

export default Footer;
